import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, Download, Home, CreditCard, AlertTriangle } from 'lucide-react';

const SuccessContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 120px 20px 80px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessCard = styled.div`
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
  border-top: 4px solid #7F1B1C;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.warning ? '#f59e0b' : '#10b981'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  animation: scaleIn 0.5s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
`;

const WarningBox = styled.div`
  background: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  text-align: left;
`;

const WarningTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #92400e;
  font-weight: 600;
  margin-bottom: 12px;
`;

const WarningText = styled.p`
  color: #78350f;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 4px 0;
`;

const SupportBox = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #fbbf24;
`;

const SupportText = styled.p`
  color: #78350f;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 4px 0;
  
  strong {
    color: #92400e;
  }
`;

const Title = styled.h1`
  color: #1e293b;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  color: #64748b;
  font-size: 1.125rem;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const DetailBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: #1e293b;
  font-size: 1rem;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #7F1B1C 0%, #640003 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #640003 0%, #4a0002 100%);
    box-shadow: 0 10px 25px rgba(127, 27, 28, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: #7F1B1C;
  border: 2px solid #7F1B1C;

  &:hover {
    background: #7F1B1C;
    color: white;
    box-shadow: 0 10px 20px rgba(127, 27, 28, 0.2);
  }
`;

const KPCardRechargeSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [transactionDetails, setTransactionDetails] = useState(null);

  useEffect(() => {
    // Parse transaction details from Adumo's callback parameters
    const kpCardNumber = searchParams.get('VARIABLE1') || searchParams.get('Variable1') || 'N/A';
    const maskedCard = searchParams.get('_PANHASHED') || '';
    
    // Check O'City top-up status
    const topUpSuccess = searchParams.get('topUpSuccess') === 'true';
    const topUpError = searchParams.get('topUpError');
    const newBalance = searchParams.get('newBalance');
    const oCityOrderNumber = searchParams.get('oCityOrderNumber');
    
    const details = {
      reference: searchParams.get('_MERCHANTREFERENCE') || searchParams.get('REFERENCE') || 'N/A',
      amount: searchParams.get('TXTPRICE') || searchParams.get('_AMOUNT') || searchParams.get('AMOUNT') || 'N/A',
      currency: searchParams.get('_CURRENCYCODE') || searchParams.get('CURRENCY') || 'NAD',
      transactionId: searchParams.get('_TRANSACTIONINDEX') || searchParams.get('TRANSACTIONID') || 'N/A',
      cardNumber: kpCardNumber !== 'N/A' ? kpCardNumber : maskedCard,
      paymentCard: maskedCard, // The card used for payment
      date: searchParams.get('_ACQUIRERDATETIME') || new Date().toLocaleString(),
      status: searchParams.get('_STATUS') || 'Successful',
      bankMessage: searchParams.get('_BANK_ERROR_MESSAGE') || '',
      cardInstitution: searchParams.get('_CARDINSTITUTION') || '',
      cardType: searchParams.get('_CARDTYPE') || '',
      // O'City top-up status
      topUpSuccess: topUpSuccess,
      topUpError: topUpError,
      newBalance: newBalance,
      oCityOrderNumber: oCityOrderNumber
    };
    
    // Log all parameters for debugging
    console.log('Success page parameters:', Object.fromEntries(searchParams.entries()));
    console.log('Parsed transaction details:', details);
    
    setTransactionDetails(details);
  }, [searchParams]);

  const handleDownloadReceipt = () => {
    if (!transactionDetails) return;

    let receiptContent = `
KP CARD RECHARGE RECEIPT
========================

Transaction ID: ${transactionDetails.transactionId}
Reference: ${transactionDetails.reference}
KP Card Number: ${transactionDetails.cardNumber}
Amount: ${transactionDetails.currency} ${transactionDetails.amount}
Payment Status: ${transactionDetails.status}
Date: ${transactionDetails.date}
`;

    if (transactionDetails.topUpSuccess && transactionDetails.newBalance) {
      receiptContent += `
Top-Up Status: SUCCESSFUL
New Balance: ${transactionDetails.currency} ${transactionDetails.newBalance}
${transactionDetails.oCityOrderNumber ? `O'City Order: ${transactionDetails.oCityOrderNumber}` : ''}
`;
    } else if (transactionDetails.topUpError) {
      receiptContent += `
Top-Up Status: FAILED - CONTACT SUPPORT
Error: ${transactionDetails.topUpError}

IMPORTANT: Your payment was successful but card credit failed.
Please contact support with this reference number.
`;
    }

    receiptContent += `

Thank you for using KeyPlot Parking!
For support, contact: +264 857 208 426 / +264 61 277 824
Email: support@keyplotparking.com
    `.trim();

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `KP_Card_Recharge_${transactionDetails.reference}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <SuccessContainer>
      <SuccessCard>
        <IconContainer warning={transactionDetails && !transactionDetails.topUpSuccess && transactionDetails.topUpError}>
          {transactionDetails && !transactionDetails.topUpSuccess && transactionDetails.topUpError ? (
            <AlertTriangle size={48} color="white" />
          ) : (
            <CheckCircle size={48} color="white" />
          )}
        </IconContainer>

        <Title>
          {transactionDetails && !transactionDetails.topUpSuccess && transactionDetails.topUpError
            ? 'Payment Successful - Action Required'
            : 'Payment Successful!'}
        </Title>
        <Message>
          {transactionDetails && !transactionDetails.topUpSuccess && transactionDetails.topUpError ? (
            <>Your payment was successful, but we encountered an issue crediting your KP Card. Please contact support with your reference number.</>
          ) : (
            <>Your KP Card has been recharged successfully. You can now use your card for parking payments.</>
          )}
        </Message>

        {transactionDetails && !transactionDetails.topUpSuccess && transactionDetails.topUpError && (
          <WarningBox>
            <WarningTitle>
              <AlertTriangle size={20} />
              Card Top-Up Failed
            </WarningTitle>
            <WarningText>
              Your payment of {transactionDetails.currency} {transactionDetails.amount} was successfully processed by the payment gateway.
            </WarningText>
            <WarningText>
              <strong>However, we could not credit your KP Card balance due to a technical issue.</strong>
            </WarningText>
            <WarningText>
              Error: {transactionDetails.topUpError}
            </WarningText>
            <SupportBox>
              <WarningText>
                <strong>Don't worry! No money was lost.</strong> Your funds are safe and will be credited to your card.
              </WarningText>
              <WarningText>
                Please contact our support team with your reference number:
              </WarningText>
              <SupportText>
                <strong>Phone:</strong> +264 857 208 426 / +264 61 277 824
              </SupportText>
              <SupportText>
                <strong>Email:</strong> support@keyplotparking.com
              </SupportText>
              <SupportText>
                <strong>Reference:</strong> {transactionDetails.reference}
              </SupportText>
            </SupportBox>
          </WarningBox>
        )}

        {transactionDetails && transactionDetails.topUpSuccess && transactionDetails.newBalance && (
          <WarningBox style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
            <WarningTitle style={{ color: '#166534' }}>
              <CheckCircle size={20} />
              Card Recharged Successfully
            </WarningTitle>
            <WarningText style={{ color: '#166534' }}>
              <strong>New Balance:</strong> {transactionDetails.currency} {transactionDetails.newBalance}
            </WarningText>
            {transactionDetails.oCityOrderNumber && (
              <WarningText style={{ color: '#166534' }}>
                <strong>O'City Order:</strong> {transactionDetails.oCityOrderNumber}
              </WarningText>
            )}
          </WarningBox>
        )}

        {transactionDetails && (
          <DetailBox>
            <DetailRow>
              <DetailLabel>Transaction ID</DetailLabel>
              <DetailValue>{transactionDetails.transactionId}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Reference</DetailLabel>
              <DetailValue>{transactionDetails.reference}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>KP Card Number</DetailLabel>
              <DetailValue>{transactionDetails.cardNumber}</DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Amount</DetailLabel>
              <DetailValue>
                {transactionDetails.currency} {transactionDetails.amount}
              </DetailValue>
            </DetailRow>
            <DetailRow>
              <DetailLabel>Payment Status</DetailLabel>
              <DetailValue style={{ color: '#10b981' }}>
                {transactionDetails.status}
              </DetailValue>
            </DetailRow>
            {transactionDetails.topUpError && (
              <DetailRow>
                <DetailLabel>Top-Up Status</DetailLabel>
                <DetailValue style={{ color: '#f59e0b' }}>
                  Failed - Contact Support
                </DetailValue>
              </DetailRow>
            )}
            {transactionDetails.topUpSuccess && transactionDetails.newBalance && (
              <DetailRow>
                <DetailLabel>New KP Card Balance</DetailLabel>
                <DetailValue style={{ color: '#10b981' }}>
                  {transactionDetails.currency} {transactionDetails.newBalance}
                </DetailValue>
              </DetailRow>
            )}
            {transactionDetails.paymentCard && transactionDetails.paymentCard !== 'N/A' && (
              <DetailRow>
                <DetailLabel>Payment Card</DetailLabel>
                <DetailValue>
                  {transactionDetails.cardInstitution && `${transactionDetails.cardInstitution} `}
                  {transactionDetails.paymentCard}
                </DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Date & Time</DetailLabel>
              <DetailValue>{transactionDetails.date}</DetailValue>
            </DetailRow>
          </DetailBox>
        )}

        <ButtonGroup>
          <SecondaryButton onClick={handleDownloadReceipt}>
            <Download size={20} />
            Download Receipt
          </SecondaryButton>
          <PrimaryButton onClick={() => navigate('/kp-card/recharge')}>
            <CreditCard size={20} />
            Recharge Again
          </PrimaryButton>
        </ButtonGroup>

        <ButtonGroup>
          <SecondaryButton onClick={() => navigate('/')}>
            <Home size={20} />
            Go to Home
          </SecondaryButton>
        </ButtonGroup>
      </SuccessCard>
    </SuccessContainer>
  );
};

export default KPCardRechargeSuccess;

